<template>
  <div class="employee-page-header">
    <div class="employee-header-top">
      <ExBreadcrumbs :breadcrumbsList="breadcrumbs" @onClickBreadcrumb="handleBreadcrumbClick" />
    </div>
    <div class="employee-header-content d-flex justify-space-between align-center">
      <div class="employee-details-section">
        <div class="employee-details d-flex align-center">
          <ExAvatar
            :picture="avatar"
            :firstName="firstName"
            :lastName="lastName"
            size="large"
            class="employee-avatar"
          ></ExAvatar>
          <div class="employee-info">
            <div class="employee-name">{{ firstName }} {{ lastName }} <div class="employee-title">{{ title }}</div></div>
            <div class="employee-badges">
              <EmployeeBadge
                :archived="archived"
                :access="employeeAccess"
                :status="status"
                :showTooltip="true"
                :hideDot="false"
              />
              <span v-if="!tracking" class="not-tracking">
                Not Tracking
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="readinessProductOn && readiness != null" class="employee-readiness-section">
        <EmployeeReadinessHeaderBox
          :readiness="readiness"
          :last_month_readiness="last_month_readiness"
          :recalculating="readinessRecalc.pending"
          :recalcFailed="readinessRecalc.failed"
        />
      </div>
      <div class="employee-actions-section d-flex justify-space-between align-center gap-1">
        <ChatterIcon
          v-if="chatterFeed.enabled.value"
          class="employee-card-header-chatter-link"
          :unseen_mention_count="chatterFeed.unseenMentionCount.value"
          @click="openChatter()"
        />
        <a
          v-else
          @click="openChatter()"
          class="employee-card-header-chatter-link"
        >
          <img src="@components/assets/icons/chatter.svg" class="chatter-type-icon-employee" alt="chatter" />
        </a>
        <EmployeeHorizontalSideBar 
          @employeeUpdated="handleEmployeeUpdate"
          @employeeStatusChanged="handleEmployeeStatusChanged" 
          @employeeDeleted="handleEmployeeDeleted" 
        />
      </div>
    </div>
    <ChatterDrawer
      :isEmployeeChatter="true"
      ref="drawerContainer"
      :drawer="chatterDrawer.show"
      :avatar="{
        picture: avatar,
        firstName: firstName,
        lastName: lastName,
      }"
      :typeValue="name"
      @closeDrawer="closeChatterDrawer"
      :type="'Employee'"
      :chatterList="chatterDrawer.items"
      :totalCheckpointChatter="chatterDrawer.total"
      @requestMoreCheckpointsChatter="loadMoreChatter"
      :enable-combined-drawer="chatterFeed.enabled.value"
      :feed-items="chatterFeed.items.value"
      :has-more="chatterFeed.hasMore.value"
      object-type="employee"
      :object-id="chatterObjectId"
      :loading="chatterFeed.enabled.value && chatterFeed.loading.value"
      @loadOlder="chatterFeed.loadOlder()"
      @posted="chatterFeed.refresh()"
      @changed="chatterFeed.refresh()"
      @filterChange="chatterFeed.setFilter($event)"
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed, onMounted, onUnmounted, getCurrentInstance, watch } from 'vue';
import EmployeeBadge from "@components/organizations/employeeStatusBadge/EmployeeBadge.vue";
import EmployeeHorizontalSideBar from "@components/common/EmployeeHorizontalSideBar.vue";
import ChatterDrawer from "@components/ChatterDrawer/ChatterDrawer.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import { useChatterFeed } from "@components/ChatterDrawer/composables/useChatterFeed";
import ExAvatar from "@components/common/ExAvatar.vue";
import EmployeeReadinessHeaderBox from "@components/EmployeeReadinessHeaderBox/EmployeeReadinessHeaderBox.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import { getChatterList } from "@/api";
import { shouldShowOrganizationEmployeeReadinessTabs } from "@/utils/organizationEmployeeDetailTabs";

const props = defineProps({
  status: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  avatar: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: null
  },
  archived: {
    type: Boolean,
    default: false
  },
  tracking: {
    type: Boolean,
    default: true
  },
  readiness: {
    type: [Number, null],
    default: 0
  },
  last_month_readiness: {
    type: [Number, null],
    default: 0
  },
  breadcrumbs: {
    type: Array,
    default: () => []
  },
  employeeId: {
    type: [String, Number],
    default: null
  },
  employeeData: {
    type: Object,
    default: () => ({})
  },
  readinessRecalc: {
    type: Object,
    default: () => ({ pending: false, failed: false })
  }
});

const instance = getCurrentInstance();
const { proxy } = instance;

const chatterDrawer = ref({
  show: false,
  items: [],
  total: 0,
  page: 1,
  loading: false,
});

const firstName = computed(() => { 
  return props.name.split(' ')[0];
});

const lastName = computed(() => {
  const nameParts = props.name.split(' ');
  return nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
});

const readinessProductOn = computed(() =>
  shouldShowOrganizationEmployeeReadinessTabs(proxy?.$root?.currentAccountDetails)
);

const getEmployeeAccess = (employee) => {
  const access = employee?.access;
  if (
    access?.is_suspended ||
    access?.state === "suspended" ||
    employee?.is_suspended ||
    access?.employee_status === "suspended" ||
    employee?.isSuspended
  ) {
    return "suspended";
  } else {
    return access ? access.employee_status : "no-access";
  }
};

const employeeAccess = computed(() => {
  return getEmployeeAccess(props.employeeData);
});

const chatterObjectId = computed(() => props.employeeId || props.employeeData?.id || null);

const chatterFeed = useChatterFeed({
  objectType: "employee",
  getObjectId: () => chatterObjectId.value,
});

const openChatter = async () => {
  chatterDrawer.value.show = true;
  if (chatterFeed.enabled.value) {
    await chatterFeed.refresh();
    return;
  }
  chatterDrawer.value.items = [];
  chatterDrawer.value.page = 1;
  chatterDrawer.value.total = 0;
  await fetchChatter();
};

const closeChatterDrawer = () => {
  chatterDrawer.value.show = false;
};

const fetchChatter = async () => {
  if (chatterDrawer.value.loading) return;

  chatterDrawer.value.loading = true;
  proxy.$loader.show();
  try {
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    const empId = props.employeeId || props.employeeData?.id || null;
    
    if (!empId) {
      console.error("Employee ID not available");
      return;
    }

    const { data } = await getChatterList(
      accountID,
      empId,
      "employee",
      chatterDrawer.value.page
    );

    if (data && data.count) {
      chatterDrawer.value.total = data.count;
    }
    if (data && data.results) {
      let chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        let nameParts = el.recorded_by_name.split(" ");
        const newEl = {};
        newEl.id = el.id;
        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }
        newEl.IsExIQtiveBot = false;
        newEl.message = el.note;
        newEl.created_on = millisecondsString;
        newEl.IsHistory = el.is_history;
        return newEl;
      });
      chatterDrawer.value.items.push(...chatterItems);
    }
  } catch (error) {
    console.error("Failed to fetch chatter", error);
    proxy.$notifications?.error("Failed to fetch chatter data.");
  } finally {
    chatterDrawer.value.loading = false;
    proxy.$loader.hide();
  }
};

const loadMoreChatter = () => {
  if (chatterDrawer.value.items.length < chatterDrawer.value.total) {
    chatterDrawer.value.page++;
    fetchChatter();
  }
};

const handleEmployeeUpdate = (employeeData) => {
  emit('employeeUpdated', employeeData);
};

const handleEmployeeStatusChanged = (employeeData) => {
  emit('employeeStatusChanged', employeeData);
};

const handleEmployeeDeleted = () => {
  emit('employeeDeleted');
};

const handleBreadcrumbClick = (breadcrumb) => {
  emit('breadcrumb-click', breadcrumb);
};

const emit = defineEmits(['employeeUpdated', 'employeeStatusChanged', 'employeeDeleted', 'optionClick', 'requestMoreCheckpointsChatter', 'breadcrumb-click']);

onMounted(async () => {
  if (await chatterFeed.resolveFlag()) await chatterFeed.refresh();
});

watch(
  () => props.employeeId || props.employeeData?.id,
  async (newId, oldId) => {
    if (!chatterFeed.enabled.value || !newId || newId === oldId) return;
    chatterFeed.reset();
    await chatterFeed.refresh();
  }
);

onUnmounted(() => {
});

</script>
<style scoped>
.employee-page-header .employee-header-content {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
}
.employee-page-header .employee-details-section {
  flex: 1;
  min-width: 0;
}
.employee-page-header .employee-details {
  display: flex;
  align-items: center;
}
.employee-page-header .employee-readiness-section {
  flex: 0 0 auto;
  min-width: 200px;
  margin: 0 30px;
}
.employee-page-header .employee-actions-section {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}
.employee-page-header .employee-card-header-chatter-link {
  position: relative;
  top: 5px;
}
.employee-page-header .chatter-type-icon-employee {
  padding: 6px;
  text-align: center;
  width: fit-content;
  cursor: url("../assets/cursorArrow.svg"), auto;
}
.employee-page-header .chatter-type-icon-employee:hover {
  background-color: #f2edff;
  padding: 6px;
  text-align: center;
  box-shadow: 0px 4px 4px 0px #0000000f;
  border-radius: 8px;
}
::v-deep.employee-page-header .employee-readiness-section .readiness-display {
  width: 100%;
}
::v-deep.employee-page-header .employee-readiness-section .new-progress-bar-container {
  width: 100%;
}
.employee-page-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.employee-page-header .employee-header-top {
  margin-bottom: 16px;
}
.employee-page-header .employee-details {
  display: flex;
  align-items: center;
  gap: 12px;
}
.employee-page-header .employee-info {
  display: flex;
  flex-direction: column;
}
.employee-page-header .employee-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
}
.employee-page-header .employee-title {
  font-size: 14px;
  font-weight: 400;
  color: #9ca5b6;
  margin-top: 2px;
}
.employee-page-header .employee-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.employee-page-header .header-org-menu {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-around;
}
.employee-page-header .header-chatter-icon {
  width: 24px;
  height: 24px;
}
.employee-page-header .employee-avatar {
  height: 50px !important;
  min-width: 50px !important;
  width: 50px !important;
}
.employee-page-header .employee-menu-column {
  position: relative;
  right: 15px;
  top: 5px;
}
.employee-page-header .not-tracking {
  width: auto;
  height: 22px;
  border: 1px solid #eaecf0;
  background-color: #f9fafb;
  color: #344054;
  text-transform: capitalize;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0em;
  text-align: center;
  padding: 2px 8px 2px 8px !important;
  border-radius: 1000px;
}
</style>